import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { Frame, Trash2, Loader2, Plus, Pencil, X, Save } from "lucide-react";

const EMPTY = {
  serie_profil: "",
  tip_bagheta: "standard",
  material_vitraj: "sticla",
  grosime_vitraj: "",
  grosime_bagheta: "",
};

export default function BaghetePage() {
  const [items, setItems] = useState([]);
  const [optiuni, setOptiuni] = useState({ serii: [], tipuri: ["standard"], materiale: ["sticla", "tablie"] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, opt] = await Promise.all([apiGet("/baghete"), apiGet("/baghete/optiuni")]);
      setItems(lst.items || []);
      setOptiuni(opt || { serii: [], tipuri: ["standard"], materiale: ["sticla", "tablie"] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      serie_profil: form.serie_profil.trim(),
      tip_bagheta: (form.tip_bagheta || "standard").trim().toLowerCase(),
      material_vitraj: form.material_vitraj,
      grosime_vitraj: parseInt(form.grosime_vitraj, 10),
      grosime_bagheta: parseInt(form.grosime_bagheta, 10),
    };
    try {
      if (editId) {
        await apiPut(`/baghete/${editId}`, payload);
        toast.success("Configurare actualizata");
      } else {
        await apiPost("/baghete", payload);
        toast.success("Configurare adaugata");
      }
      resetForm();
      await load();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      serie_profil: r.serie_profil || "",
      tip_bagheta: r.tip_bagheta || "standard",
      material_vitraj: r.material_vitraj || "sticla",
      grosime_vitraj: String(r.grosime_vitraj ?? ""),
      grosime_bagheta: String(r.grosime_bagheta ?? ""),
    });
  };

  const remove = async (r) => {
    if (!window.confirm(`Stergeti configurarea ${r.serie_profil} / ${r.material_vitraj} / ${r.grosime_vitraj}mm?`)) return;
    try {
      await apiDelete(`/baghete/${r.id}`);
      toast.success("Configurare stearsa");
      setItems((prev) => prev.filter((x) => x.id !== r.id));
      if (editId === r.id) resetForm();
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  const inp = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500";
  const lbl = "block text-slate-600 text-xs mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-6" data-testid="baghete-page">
      <div className="flex items-center gap-3">
        <Frame className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Configurare baghete</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-3">
        Grosimea baghetei per serie profil, material vitraj si grosime vitraj. Folosita in Prod-2.
      </p>

      <form
        onSubmit={submit}
        className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
        data-testid="baghete-form"
      >
        <div>
          <label className={lbl}>Serie profil</label>
          <input
            required list="serii-list" value={form.serie_profil}
            onChange={(e) => setForm({ ...form, serie_profil: e.target.value })}
            data-testid="baghete-serie-input" className={inp} placeholder="Amel-71"
          />
          <datalist id="serii-list">
            {(optiuni.serii || []).map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Tip bagheta</label>
          <input
            list="tipuri-list" value={form.tip_bagheta}
            onChange={(e) => setForm({ ...form, tip_bagheta: e.target.value })}
            data-testid="baghete-tip-input" className={inp} placeholder="standard"
          />
          <datalist id="tipuri-list">
            {(optiuni.tipuri || []).map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Material vitraj</label>
          <select
            value={form.material_vitraj}
            onChange={(e) => setForm({ ...form, material_vitraj: e.target.value })}
            data-testid="baghete-material-select" className={inp}
          >
            {(optiuni.materiale || ["sticla", "tablie"]).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Grosime vitraj (mm)</label>
          <input
            required type="number" min="1" value={form.grosime_vitraj}
            onChange={(e) => setForm({ ...form, grosime_vitraj: e.target.value })}
            data-testid="baghete-grosime-vitraj-input" className={inp} placeholder="26"
          />
        </div>
        <div>
          <label className={lbl}>Grosime bagheta (mm)</label>
          <input
            required type="number" min="1" value={form.grosime_bagheta}
            onChange={(e) => setForm({ ...form, grosime_bagheta: e.target.value })}
            data-testid="baghete-grosime-input" className={inp} placeholder="22"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="submit" disabled={saving}
            data-testid="baghete-save-btn"
            className="flex-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editId ? "Salveaza" : "Adauga"}
          </button>
          {editId && (
            <button
              type="button" onClick={resetForm}
              data-testid="baghete-cancel-btn"
              className="rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 px-3 py-2 text-sm transition-colors"
              title="Anuleaza editarea"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se incarca...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="baghete-empty">
            Nicio configurare de baghete inca.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="baghete-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-4 py-3 font-medium">Serie</th>
                <th className="px-4 py-3 font-medium">Tip bagheta</th>
                <th className="px-4 py-3 font-medium">Material</th>
                <th className="px-4 py-3 font-medium text-center">Grosime vitraj</th>
                <th className="px-4 py-3 font-medium text-center">Grosime bagheta</th>
                <th className="px-4 py-3 font-medium text-right">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`baghete-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.serie_profil}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">{r.tip_bagheta}</span>
                  </td>
                  <td className="px-4 py-3 capitalize">{r.material_vitraj}</td>
                  <td className="px-4 py-3 text-center">{r.grosime_vitraj} mm</td>
                  <td className="px-4 py-3 text-center font-semibold text-amber-700">{r.grosime_bagheta} mm</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => startEdit(r)}
                        data-testid={`baghete-edit-${r.id}`}
                        className="text-slate-500 hover:text-amber-600 transition-colors" title="Editeaza"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`baghete-delete-${r.id}`}
                        className="text-red-500 hover:text-red-700 transition-colors" title="Sterge"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
